/**
 * Das Geräte-Objektblatt (P6 Block D).
 *
 * Löst die alte Ingest-Detailseite ab. Der wichtigste Unterschied ist nicht das
 * Aussehen, sondern was hier NICHT steht: Die Zugangsdaten der Anlagen-Freigabe
 * verlassen den Server nicht mehr (Ingest-Review I2) — das Blatt zeigt nur, ob
 * welche hinterlegt sind. Der Freigabe-Weg folgt demselben Gedanken wie die
 * Terminal-Kopplung: Ein Gerät wird nicht durch das Aufsetzen zur
 * Vertrauensstelle, sondern durch die ausdrückliche Freigabe hier.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Kennung, Notiz, Zustand } from '../../components/leitstand';

const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;

interface Geraet {
  id: string;
  deviceId: string;
  hostname?: string | null;
  status: string;
  role?: string | null;
  mac?: string | null;
  piModel?: string | null;
  piSerial?: string | null;
  requestedAt?: string | null;
  lastSeenAt?: string | null;
  apiKeyIssuedAt?: string | null;
  configVersion?: number | null;
  lastConfigAppliedVersion?: number | null;
  lastOsRelease?: string | null;
  bannedReason?: string | null;
  anlagen?: Array<{ maschine: string; bezeichnung: string; feeds: number }>;
  quelle?: string | null;
  zugangsdatenHinterlegt?: boolean;
}

const STATUS_TEXT: Record<string, string> = {
  APPROVED: 'freigegeben',
  PENDING: 'wartet auf Freigabe',
  REJECTED: 'abgelehnt',
  BANNED: 'gesperrt',
  DECOMMISSIONED: 'ausgemustert',
};

function zeitpunkt(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function GeraetBlatt() {
  const { user } = useAuth();
  const { id = '' } = useParams();

  const [geraet, setGeraet] = useState<Geraet | null>(null);
  const [verwendung, setVerwendung] = useState<
    Array<{ text: string; wert: number; ziel?: string }>
  >([]);
  const [verlauf, setVerlauf] = useState<Array<{ wann: string; wer: string; was: string }>>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [meldung, setMeldung] = useState('');
  const [sende, setSende] = useState(false);
  const [freigabeSchritt, setFreigabeSchritt] = useState(false);
  const [technischOffen, setTechnischOffen] = useState(false);

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const [oRes, vRes, hRes] = await Promise.all([
        fetch(`/api/einrichten/objekt/geraete/${id}`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/geraete/${id}/verwendung`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/geraete/${id}/verlauf`, { credentials: 'include' }),
      ]);
      if (!oRes.ok) throw new Error('Das Gerät konnte nicht geladen werden');
      setGeraet((await oRes.json()).objekt ?? null);
      if (vRes.ok) setVerwendung((await vRes.json()).zeilen ?? []);
      if (hRes.ok) setVerlauf((await hRes.json()).zeilen ?? []);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das Gerät konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [id]);

  useEffect(() => {
    void lade();
  }, [lade]);

  async function ruf(pfad: string, erfolg: string) {
    setSende(true);
    setMeldung('');
    try {
      const res = await fetch(`/api/admin/ingest-devices/${id}/${pfad}`, {
        method: 'POST',
        credentials: 'include',
      });
      const antwort = await res.json().catch(() => null);
      if (!res.ok) {
        const codes: Record<string, string> = {
          TWO_FACTOR_REQUIRED: 'Dafür ist die Zwei-Faktor-Anmeldung nötig — bitte neu anmelden.',
          FORBIDDEN: 'Dafür fehlt die Berechtigung.',
        };
        throw new Error(codes[antwort?.code] || antwort?.error || 'Hat nicht geklappt');
      }
      setMeldung(erfolg);
      setFreigabeSchritt(false);
      await lade();
      setTimeout(() => setMeldung(''), 4000);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Hat nicht geklappt');
    } finally {
      setSende(false);
    }
  }

  if (laden && !geraet) {
    return (
      <LeitstandRahmen titel="Gerät" benutzer={user?.displayName}>
        <p className="py-12 text-center text-sm text-tinte-3">Lade …</p>
      </LeitstandRahmen>
    );
  }

  if (!geraet) {
    return (
      <LeitstandRahmen titel="Gerät" benutzer={user?.displayName}>
        <div className="flex flex-col gap-4">
          <Link to="/einrichten/geraete" className="text-[13px] text-tinte-3 hover:text-tinte-2">
            ← Geräte
          </Link>
          <Notiz titel="Nicht gefunden">{fehler || 'Dieses Gerät gibt es nicht (mehr).'}</Notiz>
        </div>
      </LeitstandRahmen>
    );
  }

  const freigegeben = geraet.status === 'APPROVED';
  const wartet = geraet.status === 'PENDING';

  return (
    <LeitstandRahmen titel={geraet.deviceId} benutzer={user?.displayName}>
      <div className="flex flex-col gap-6">
        <Link to="/einrichten/geraete" className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← Geräte
        </Link>

        {fehler && <Notiz titel="Fehler">{fehler}</Notiz>}
        {meldung && <p className="text-[13px] text-lauf">✓ {meldung}</p>}

        {/* Kopf: Zustand und die Handlung, die dazu passt */}
        <Karte
          titel="Zustand"
          marke={geraet.role === 'TWA' ? 'liest die Anlagen-Daten ein' : (geraet.role ?? '')}
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {freigegeben && <Zustand art="gut">freigegeben</Zustand>}
              {wartet && <Zustand art="warnung">wartet auf Freigabe</Zustand>}
              {!freigegeben && !wartet && (
                <span className="font-zahl text-xs text-tinte-3">
                  {STATUS_TEXT[geraet.status] ?? geraet.status}
                </span>
              )}
              <span className="text-sm text-tinte-2">
                zuletzt gemeldet {zeitpunkt(geraet.lastSeenAt)}
              </span>
            </div>

            {wartet && !freigabeSchritt && (
              <button onClick={() => setFreigabeSchritt(true)} className={KNOPF_PRIMAER}>
                Gerät freigeben
              </button>
            )}
            {freigegeben && (
              <button
                onClick={() =>
                  void ruf('ban', 'Gerät gesperrt — es entfernt sich beim nächsten Melden selbst.')
                }
                disabled={sende}
                className={KNOPF_LEISE}
              >
                Außer Dienst stellen
              </button>
            )}
          </div>

          {wartet && freigabeSchritt && (
            <div className="mt-4 flex flex-col gap-3 rounded-leitstand border border-linie bg-flaeche-2 p-4">
              <p className="text-sm text-tinte-2">
                Vor der Freigabe abgleichen, dass die Angaben zu dem Gerät passen, das aufgestellt
                wurde — danach erhält es seinen Schlüssel und die Angaben zu den Anlagen.
              </p>
              <dl className="grid gap-2 text-sm [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">Kennung</dt>
                  <dd className="font-zahl">{geraet.deviceId}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">
                    Rechnername
                  </dt>
                  <dd className="font-zahl">{geraet.hostname ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">
                    Netzwerkadresse
                  </dt>
                  <dd className="font-zahl">{geraet.mac ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">Modell</dt>
                  <dd className="font-zahl">{geraet.piModel ?? '—'}</dd>
                </div>
              </dl>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    void ruf('approve', 'Gerät freigegeben — es holt sich jetzt seinen Schlüssel.')
                  }
                  disabled={sende}
                  className={KNOPF_PRIMAER}
                >
                  Freigeben
                </button>
                <button
                  onClick={() => setFreigabeSchritt(false)}
                  disabled={sende}
                  className={KNOPF_LEISE}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          )}

          {geraet.bannedReason && (
            <p className="mt-3 text-[13px] text-tinte-3">Grund: {geraet.bannedReason}</p>
          )}
        </Karte>

        {/* Woher die Daten kommen */}
        <Karte titel="Anlagen" marke="welche Daten dieses Gerät holt">
          {geraet.anlagen?.length ? (
            <div className="flex flex-col gap-3">
              <ul className="flex flex-col gap-2">
                {geraet.anlagen.map((a) => (
                  <li key={a.maschine} className="flex flex-wrap items-baseline gap-2 text-sm">
                    <Kennung>{a.bezeichnung}</Kennung>
                    <span className="text-tinte-3">{a.feeds} Datenarten</span>
                  </li>
                ))}
              </ul>
              <p className="text-[13px] text-tinte-3">
                Gelesen wird von {geraet.quelle ?? 'der eingestellten Freigabe'}.{' '}
                {geraet.zugangsdatenHinterlegt
                  ? 'Zugangsdaten sind hinterlegt und bleiben im System.'
                  : 'Es sind keine Zugangsdaten hinterlegt — die Freigabe wird ohne Anmeldung gelesen.'}
              </p>
            </div>
          ) : (
            <p className="text-sm text-tinte-3">Diesem Gerät ist noch keine Anlage zugeordnet.</p>
          )}
        </Karte>

        {/* Verwendung — was hängt an diesem Gerät */}
        <Karte titel="Verwendung" marke="Wirkung sichtbar, bevor man handelt">
          {verwendung.length === 0 ? (
            <p className="text-sm text-tinte-3">Noch nichts erfasst.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {verwendung.map((z) => (
                <li key={z.text} className="flex items-baseline justify-between gap-4 text-sm">
                  <span className="text-tinte-2">{z.text}</span>
                  {z.ziel ? (
                    <Link to={z.ziel} className="font-zahl text-akzent hover:underline">
                      {z.wert}
                    </Link>
                  ) : (
                    <span className="font-zahl">{z.wert}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Karte>

        {/* Verlauf */}
        <Karte titel="Verlauf" marke="aus dem Protokoll, neueste zuerst">
          {verlauf.length === 0 ? (
            <p className="text-sm text-tinte-3">Noch keine Änderungen protokolliert.</p>
          ) : (
            <div className="flex flex-col">
              {verlauf.map((z, i) => (
                <div
                  key={`${z.wann}-${i}`}
                  className={`grid grid-cols-[110px_130px_1fr] gap-3 px-1 py-2.5 text-[13px] max-sm:grid-cols-[90px_1fr] ${
                    i < verlauf.length - 1 ? 'border-b border-linie' : ''
                  }`}
                >
                  <span className="font-zahl text-xs text-tinte-3">
                    {new Date(z.wann).toLocaleString('de-DE', {
                      timeZone: 'Europe/Berlin',
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span className="text-tinte-2">{z.wer}</span>
                  <span className="max-sm:col-span-2">{z.was}</span>
                </div>
              ))}
            </div>
          )}
        </Karte>

        {/* Technisches bleibt zugeklappt */}
        <Karte titel="Technische Angaben" marke={technischOffen ? '' : 'für den Störungsfall'}>
          {!technischOffen ? (
            <button
              onClick={() => setTechnischOffen(true)}
              className="text-[13px] text-tinte-3 hover:text-tinte-2"
            >
              Anzeigen
            </button>
          ) : (
            <dl className="grid gap-3 text-sm [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
              <div>
                <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">Kennung</dt>
                <dd className="font-zahl">{geraet.deviceId}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">
                  Rechnername
                </dt>
                <dd className="font-zahl">{geraet.hostname ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">
                  Netzwerkadresse
                </dt>
                <dd className="font-zahl">{geraet.mac ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">Modell</dt>
                <dd className="font-zahl">{geraet.piModel ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">
                  Seriennummer
                </dt>
                <dd className="font-zahl">{geraet.piSerial ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">
                  Betriebssystem
                </dt>
                <dd className="font-zahl">{geraet.lastOsRelease ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">
                  Erstmals gemeldet
                </dt>
                <dd className="font-zahl">{zeitpunkt(geraet.requestedAt)}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">
                  Schlüssel ausgestellt
                </dt>
                <dd className="font-zahl">{zeitpunkt(geraet.apiKeyIssuedAt)}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.13em] text-tinte-3">
                  Einstellungen
                </dt>
                <dd className="font-zahl">
                  Stand {geraet.configVersion ?? '—'}
                  {geraet.lastConfigAppliedVersion !== geraet.configVersion && (
                    <span className="text-stand">
                      {' '}
                      · Gerät hat {geraet.lastConfigAppliedVersion ?? '—'}
                    </span>
                  )}
                </dd>
              </div>
            </dl>
          )}
        </Karte>
      </div>
    </LeitstandRahmen>
  );
}
